import CustomerBirthdayOfDay from '../../Custom/Main/Customer/CustomerBirthdayOfDay';
import EmployeeBirthdayOfDay from '../../Custom/Main/Employee/EmployeeBirthdayOfDay';
import DashboardHeader from '../../Common/DashboardHeader';
import { Row, Col } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import { useBranch } from '../../../../context/BranchContext';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend, Cell
} from 'recharts';
import './Birthdays.css';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const SummaryCard = ({ label, value, detail, icon, tone }) => (
    <div className={`birthday-summary-card birthday-summary-card--${tone}`}>
        <div className="birthday-summary-card__header">
            <span>{label}</span>
            <div className="birthday-summary-card__icon">
                <i className={`fa-solid ${icon}`}></i>
            </div>
        </div>
        <strong>{value}</strong>
        <p>{detail}</p>
    </div>
);

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
        return null;
    }

    const total = payload.reduce((sum, item) => sum + item.value, 0);

    return (
        <div className="birthday-chart-tooltip">
            <strong>{label}</strong>
            {payload.map((item) => (
                <div key={item.name}>
                    <span>{item.name}</span>
                    <b>{item.value}</b>
                </div>
            ))}
            <small>Total: {total}</small>
        </div>
    );
};

const Birthdays = () => {
    const { selectedBranch } = useBranch();
    const [totalToday, setTotalToday] = useState(0);
    const [todayCustomers, setTodayCustomers] = useState(0);
    const [todayEmployees, setTodayEmployees] = useState(0);
    const [customerMonthly, setCustomerMonthly] = useState([]);
    const [employeeMonthly, setEmployeeMonthly] = useState([]);

    useEffect(() => {
        const branchId = selectedBranch ? selectedBranch.id : -1;

        axiosInstance.get(`${Api.main.customer}/total/birthdays/${branchId}`)
            .then((response) => setTotalToday(response.data || 0));

        axiosInstance.get(`${Api.main.customer}/birthdays/monthly/${branchId}`)
            .then((response) => setCustomerMonthly(response.data || []));

        axiosInstance.get(`${Api.main.employee}/birthdays/monthly/${branchId}`)
            .then((response) => setEmployeeMonthly(response.data || []));
    }, [selectedBranch]);

    const chartData = useMemo(() => MONTHS.map((name, index) => {
        const month = index + 1;
        const customers = customerMonthly.find((item) => item.month === month)?.count || 0;
        const employees = employeeMonthly.find((item) => item.month === month)?.count || 0;

        return {
            name,
            Clientes: customers,
            Colaboradores: employees
        };
    }), [customerMonthly, employeeMonthly]);

    const currentMonth = new Date().getMonth() + 1;
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;

    const thisMonthCustomers = customerMonthly.find((item) => item.month === currentMonth)?.count || 0;
    const thisMonthEmployees = employeeMonthly.find((item) => item.month === currentMonth)?.count || 0;
    const nextMonthCustomers = customerMonthly.find((item) => item.month === nextMonth)?.count || 0;
    const nextMonthEmployees = employeeMonthly.find((item) => item.month === nextMonth)?.count || 0;

    const peakMonth = chartData.reduce((best, current) => {
        const currentTotal = (current.Clientes || 0) + (current.Colaboradores || 0);
        const bestTotal = (best.Clientes || 0) + (best.Colaboradores || 0);
        return currentTotal > bestTotal ? current : best;
    }, { name: '-', Clientes: 0, Colaboradores: 0 });
    const peakMonthTotal = (peakMonth.Clientes || 0) + (peakMonth.Colaboradores || 0);
    const peakMonthIndex = chartData.findIndex((item) => item.name === peakMonth.name);

    return (
        <>
            <div className="birthday-dashboard">
                {/* Os números de hoje ficam nos cards logo abaixo. Repeti-los aqui gastava
                    a primeira dobra mostrando duas vezes a mesma informação. O cabeçalho
                    guarda o que os cards não dizem: de qual filial é a leitura. */}
                <DashboardHeader
                    compact
                    title="Aniversariantes"
                    metrics={[
                        {
                            key: 'branch',
                            label: 'Filial',
                            value: selectedBranch?.name || selectedBranch?.description || 'Todas as filiais',
                            tone: 'neutral',
                        },
                    ]}
                />

                <Row className="g-3 mb-4">
                    <Col lg={3} md={6}>
                        <SummaryCard
                            label="Hoje"
                            value={totalToday}
                            detail={`${todayCustomers} clientes e ${todayEmployees} colaboradores`}
                            icon="fa-cake-candles"
                            tone="amber"
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <SummaryCard
                            label="Este mês"
                            value={thisMonthCustomers + thisMonthEmployees}
                            detail={`${thisMonthCustomers} clientes e ${thisMonthEmployees} colaboradores`}
                            icon="fa-calendar-days"
                            tone="sky"
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <SummaryCard
                            label="Próximo mês"
                            value={nextMonthCustomers + nextMonthEmployees}
                            detail={`${nextMonthCustomers} clientes e ${nextMonthEmployees} colaboradores`}
                            icon="fa-forward"
                            tone="green"
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <SummaryCard
                            label="Pico anual"
                            value={peakMonth.name}
                            detail={peakMonthTotal > 0 ? `${peakMonthTotal} aniversariantes no mês` : 'Sem concentração relevante'}
                            icon="fa-chart-column"
                            tone="coral"
                        />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col xs={12}>
                        <div className="birthday-panel birthday-panel--chart">
                            <div className="birthday-panel__header">
                                <div>
                                    <span className="birthday-panel__eyebrow">Planejamento anual</span>
                                    <h3 className="birthday-panel__title">Distribuição de aniversariantes por mês</h3>
                                    <p className="birthday-panel__subtitle">Use esta curva para antecipar campanhas, ações internas e reforço de relacionamento.</p>
                                </div>
                            </div>

                            <div className="birthday-panel__body">
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart data={chartData} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--birthday-grid)" vertical={false} />
                                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--birthday-muted)' }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 12, fill: 'var(--birthday-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                        <Tooltip content={<ChartTooltip />} />
                                        <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                                        <Bar dataKey="Clientes" stackId="birthdays" fill="#d9903d" radius={[0, 0, 0, 0]}>
                                            {chartData.map((item, index) => (
                                                <Cell key={`${item.name}-customers`} fill={index === peakMonthIndex ? '#c96c1e' : '#d9903d'} fillOpacity={index === peakMonthIndex ? 1 : 0.82} />
                                            ))}
                                        </Bar>
                                        <Bar dataKey="Colaboradores" stackId="birthdays" fill="#9b7454" radius={[8, 8, 0, 0]}>
                                            {chartData.map((item, index) => (
                                                <Cell key={`${item.name}-employees`} fill={index === peakMonthIndex ? '#76543b' : '#9b7454'} fillOpacity={index === peakMonthIndex ? 1 : 0.88} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </Col>

                </Row>

                <Row className="g-3">
                    <Col xl={6}>
                        <div className="birthday-panel">
                            <div className="birthday-panel__header">
                                <div>
                                    <span className="birthday-panel__eyebrow">Clientes</span>
                                    <h3 className="birthday-panel__title">Aniversariantes do dia</h3>
                                    <p className="birthday-panel__subtitle">Lista pronta para contato imediato e ações de relacionamento.</p>
                                </div>
                            </div>
                            <div className="birthday-panel__body birthday-panel__body--flush">
                                <CustomerBirthdayOfDay onCountChange={setTodayCustomers} />
                            </div>
                        </div>
                    </Col>

                    <Col xl={6}>
                        <div className="birthday-panel">
                            <div className="birthday-panel__header">
                                <div>
                                    <span className="birthday-panel__eyebrow">Equipe</span>
                                    <h3 className="birthday-panel__title">Colaboradores aniversariantes</h3>
                                    <p className="birthday-panel__subtitle">Leitura rápida para reconhecimento interno e organização da operação.</p>
                                </div>
                            </div>
                            <div className="birthday-panel__body birthday-panel__body--flush">
                                <EmployeeBirthdayOfDay onCountChange={setTodayEmployees} />
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default Birthdays;
